import React from 'react';
import { Button, ModalPortal } from '@alpha-recycling/component-library';
import { addDays, endOfDay } from 'date-fns';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import { withAlphamartIntlProvider } from 'components/shared/AlphamartIntlProvider';
import { FieldDatePickerRangeRaw } from 'components/shared/Fields/FieldDatePickerRange/FieldDatePickerRange';
import { FiltersSection } from 'components/shared/forms/FiltersSection/FiltersSection';
import { ModalFormContent } from 'components/shared/forms/Form/ModalForm.styles';
import { LoadableContent } from 'components/shared/Loader';
import { formatDateWithTime } from 'helpers/dateTime/dateTime';
import { ModalFormType } from 'shared/constants';
import { getDateString } from 'shared/utils/getDateString';
import { downloadFile } from 'store/downloadFileSlice';
import { hideModalForm } from 'store/modalFormSlice';
import { useAppDispatch, useAppSelector } from 'store/shared/hooks';
import { snackBarPushFailure } from 'store/shared/snackBarSlice';
import { messages, useTypedIntl } from '../locale/messages';

const validationSchema = Yup.object().shape({
  addedAt: Yup.object()
    .shape({
      from: Yup.string().required(),
      to: Yup.string().required(),
    })
    .required(),
});

const DownloadUserShoppingCartLogsModalComponent = (): React.ReactElement => {
  const intl = useTypedIntl();
  const { isOpen, modalType, params } = useAppSelector(state => state.modalForm);
  const isFileDownloading = useAppSelector(state => state.downloadFile.isPending);
  const dispatch = useAppDispatch();

  const formikContext = useFormik({
    initialValues: {
      addedAt: { from: null, to: null },
    },
    validationSchema,
    onSubmit: async x => x,
  });
  const submitDisabled = !formikContext.isValid || Object.keys(formikContext.touched).length === 0;
  const handleCancelClick = () => {
    formikContext.resetForm();
    dispatch(hideModalForm());
  };

  const handleConfirmClick = async () => {
    try {
      const timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
      await dispatch(
        downloadFile(
          `/api/shopping-cart/export-activity-log`,
          `shoppingCart_history_${formatDateWithTime(new Date())}.xlsx`,
          {
            ...params,
            addedFrom: getDateString(formikContext.values.addedAt.from!),
            addedTo: getDateString(addDays(formikContext.values.addedAt.to!, 1)),
            timezone,
          },
        ),
      );
      dispatch(hideModalForm());
      formikContext.resetForm();
    } catch (error) {
      dispatch(snackBarPushFailure(intl.formatMessage({ id: 'Global.Error.SomethingWentWrong' })));
    }
  };

  const maxDate = endOfDay(new Date());
  const maxDateRange = { days: 30 };

  return (
    <ModalPortal
      isOpen={isOpen && modalType === ModalFormType.DownloadShoppingCartActivityLogs}
      innerPadding={false}
      rightButtons={[
        <Button
          onClick={handleCancelClick}
          variant="transparent"
          content="text"
          label={intl.formatMessage({ id: 'Global.Cancel' })}
        />,
        <Button
          onClick={handleConfirmClick}
          content="text"
          disabled={submitDisabled}
          label={intl.formatMessage({ id: 'Global.DownloadLogs.Confirm' })}
        />,
      ]}
    >
      <LoadableContent mode={LoadableContent.MODE.FULL} loading={isFileDownloading} />
      <ModalFormContent>
        <FormikProvider value={formikContext}>
          <FiltersSection
            noPadding
            label={intl.formatMessage({ id: 'Global.DownloadLogs.SelectDateRange' })}
          >
            <FieldDatePickerRangeRaw
              name="addedAt"
              value={formikContext.values.addedAt}
              maxDateRange={maxDateRange}
              max={maxDate}
              isClearable
              dataTestId="addedAt"
            />
          </FiltersSection>
        </FormikProvider>
      </ModalFormContent>
    </ModalPortal>
  );
};

export const DownloadUserShoppingCartLogsModal = withAlphamartIntlProvider(
  DownloadUserShoppingCartLogsModalComponent,
  messages,
);
